import AppDialog from '../../components/app_dialog'
import { Button, ProgressBar } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import { CheckCircle } from '@phosphor-icons/react'
import { useRef, useState } from 'react'
import useStore from '../../hooks/use_store'
import { humanFileSize } from '../../helpers/human_file_size'
import { useParams } from 'react-router-dom'
import generateSHA256FromImage from '../../helpers/generate_sha256'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useQuery } from '@tanstack/react-query'
import { DISABLED_REASONS } from '../../constants/disabled_reasons'
import DisabledTooltip from '../../components/disabled_tooltip'

const UploadImagesModal = () => {
  const inputRef = useRef()

  const [files, setFiles] = useState()
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [complete, setComplete] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const { addMessage } = useSnackbar()

  const { cycleID, airlineID } = useParams()
  const { createModel, updateModel } = useStore()
  const { data: cycle } = useQuery(['cycles/', cycleID])

  const createImageModel = async (index) => {
    const file = files[index]
    let sha256 = null

    try {
      sha256 = await generateSHA256FromImage(file)
    } catch (error) {
      addMessage(`Error generating sha256 for '${file.name}': ${error}`, 'error')
      setIsUploading(false)
      return
    }

    createModel.mutate(
      {
        endpoint: '/asset-images',
        silent: true,
        data: {
          file_name: file.name,
          size: file.size,
          mime: file.type,
          cycle_id: cycleID,
          airline_id: airlineID,
          sha256,
        },
      },
      {
        onSuccess: (response) => {
          if (response.data.signed_upload_url) {
            uploadToS3(response.data.signed_upload_url, file, response.data.asset_image.id)
          } else {
            // handle error?
          }
        },
        onSettled: () => {
          setIsUploading(false)
          if (index + 1 < files.length) {
            createImageModel(index + 1)
            setProgress(index + 1)
          } else {
            setComplete(true)
          }
        },
      }
    )
  }

  const handleImages = () => {
    setIsUploading(true)
    createImageModel(0)
  }

  const uploadToS3 = (signedUrl, file, assetImageID) => {
    fetch(signedUrl, {
      body: file,
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': file.type,
      },
    })
      .then(() => {
        updateModel.mutate({
          endpoint: '/asset-images',
          id: assetImageID,
          silent: true,
          data: {
            status: 'uploaded',
            cycle_id: cycleID,
          },
        })
      })
      .catch((err) => console.log(err))
  }

  const closeModal = () => {
    if (isUploading && !complete) {
      if (window.alert('Please wait for the uploads to finish before closing the modal.')) {
        return
      }
    }
    setShowModal(false)
    setComplete(false)
    setIsUploading(false)
    setFiles([])
    setProgress(0)
  }

  const columns = [
    {
      key: 'preview',
      header: 'Preview',
      render: (data) => <img src={URL.createObjectURL(data)} alt={data.name} className='max-h-10 object-cover' />,
    },
    { key: 'file_name', header: 'File name', render: (data) => data.name },
    { key: 'file_size', header: 'File size', render: (data) => humanFileSize(data.size) },
  ]

  return (
    <>
      <DisabledTooltip reason={cycle?.is_locked ? DISABLED_REASONS.cycleClosed : null}>
        <Button disabled={!!cycle?.is_locked} onClick={() => inputRef.current.click()}>
          Upload images
        </Button>
      </DisabledTooltip>
      <input
        ref={inputRef}
        type='file'
        id='file'
        multiple
        className='hidden'
        accept='image/jpeg'
        onChange={(e) => {
          setFiles(Array.from(e.target.files))
          setShowModal(true)
        }}
      />
      {showModal && (
        <AppDialog
          title='Upload images'
          onClose={closeModal}
          size='xl'
          confirmText={complete ? 'Done' : 'Upload'}
          onConfirm={complete ? () => closeModal() : () => handleImages()}
          confirmDisabled={isUploading}
        >
          {complete ? (
            <div className='bg-success-100 p-8 flex items-center justify-center flex-col rounded'>
              <CheckCircle size={40} className='text-success-600' />
              <div className='text-center text-lg font-semibold mt-4'>Upload complete!</div>
            </div>
          ) : (
            <div>
              <div className='overflow-y-scroll max-h-[400px]'>
                <DataTable
                  caption='Images to upload'
                  columns={columns}
                  rows={files ?? []}
                  rowKey={(file) => file.name}
                />
              </div>
              <div className='mt-4 px-4 space-y-2 border-t pt-4'>
                {progress > 0 && (
                  <ProgressBar value={(progress / files.length) * 100} label='Upload progress' showValue />
                )}
                <div className='text-xs flex justify-end'>
                  {progress} of {files.length} images uploaded...
                </div>
              </div>
            </div>
          )}
        </AppDialog>
      )}
    </>
  )
}

export default UploadImagesModal
